import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const SignIn: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signInWithGoogle, signInWithPhone, confirmPhoneSignIn } = useAuth() as any;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError('Échec de la connexion avec Google');
      setLoading(false);
    }
  };

  const handlePhoneSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!phoneNumber) {
      setError('Veuillez entrer un numéro de téléphone valide');
      return;
    }
    setLoading(true);
    try {
      await signInWithPhone(phoneNumber);
      setShowVerificationDialog(true);
    } catch (err) {
      setError('Échec de l\'envoi du code de vérification');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      await confirmPhoneSignIn(phoneNumber, verificationCode);
      setShowVerificationDialog(false);
      navigate('/');
    } catch (err) {
      setError('Code de vérification invalide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        py: { xs: 2, md: 4 },
        px: { xs: 1, md: 0 },
      }}
    >
      <Container maxWidth="lg">
        <Paper
          elevation={3}
          sx={{
            display: 'flex',
            overflow: 'hidden',
            borderRadius: 2,
            minHeight: isMobile ? 'auto' : 600,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Left side - Form */}
          <Box
            sx={{
              flex: 1,
              p: { xs: 3, md: 4 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ maxWidth: 400, mx: 'auto', width: '100%' }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  textAlign: 'center',
                  mb: 4,
                  fontSize: { xs: '1.75rem', md: '2.125rem' },
                }}
              >
                Connexion
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{
                  mb: 3,
                  py: { xs: 1.25, md: 1.5 },
                  backgroundColor: '#fff',
                  borderColor: '#dadce0',
                  color: '#3c4043',
                  '&:hover': {
                    backgroundColor: '#f8f9fa',
                    borderColor: '#dadce0',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: 1,
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#4285f4',
                  },
                }}
              >
                Connexion avec Google
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Ou connexion par téléphone
                </Typography>
              </Divider>

              <form onSubmit={handlePhoneSignIn}>
                <Box 
                  sx={{ 
                    mb: 2,
                    '& .PhoneInput': {
                      backgroundColor: 'background.paper',
                      borderRadius: '12px',
                      border: '2px solid #e5e7eb',
                      transition: 'all 0.3s ease-in-out',
                      overflow: 'hidden',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        borderColor: '#3b82f6',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
                        transform: 'translateY(-1px)',
                      },
                      '&:focus-within': {
                        borderColor: '#1e40af',
                        boxShadow: '0 0 0 3px rgba(30, 64, 175, 0.1)',
                        transform: 'translateY(-1px)',
                      },
                    },
                    '& .PhoneInputInput': {
                      height: { xs: '48px', md: '56px' },
                      padding: '0 16px',
                      border: 'none',
                      fontSize: { xs: '0.875rem', md: '1rem' },
                      fontWeight: '500',
                      color: 'text.primary',
                      backgroundColor: 'transparent',
                      '&:focus': {
                        outline: 'none',
                        backgroundColor: 'transparent',
                      },
                      '&::placeholder': {
                        color: 'text.secondary',
                      },
                    },
                    '& .PhoneInputCountry': {
                      marginLeft: '12px',
                    },
                  }}
                >
                  <PhoneInput
                    international
                    defaultCountry="SN"
                    value={phoneNumber}
                    onChange={(value) => setPhoneNumber(value || '')}
                    placeholder="Entrez votre numéro de téléphone"
                  />
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || !phoneNumber}
                  sx={{
                    py: { xs: 1.25, md: 1.5 },
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                    },
                    '&:disabled': {
                      background: '#e5e7eb',
                      color: '#9ca3af',
                    },
                  }}
                >
                  {loading ? 'Envoi...' : 'Recevoir le code'}
                </Button>
              </form>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Pas encore de compte ?{' '}
                  <Link
                    component={RouterLink}
                    to="/signup"
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Créer un compte
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Right side - Image/Info (hidden on mobile) */}
          {!isMobile && (
            <Box
              sx={{
                flex: 1,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                color: 'white',
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <PhoneIcon sx={{ fontSize: 80, mb: 2, opacity: 0.8 }} />
                <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
                  Bienvenue chez TotalBat
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
                  Connectez-vous pour accéder à nos services
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.8 }}>
                  Accédez à notre catalogue complet de matériaux de construction
                  et bénéficiez de nos services personnalisés.
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>

      {/* Verification Dialog */}
      <Dialog
        open={showVerificationDialog}
        onClose={() => setShowVerificationDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: { xs: 2, md: 3 },
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          Vérification du numéro
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
            Nous avons envoyé un code de vérification au {phoneNumber}
          </Typography>
          <TextField
            fullWidth
            label="Code de vérification"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Entrez le code à 6 chiffres"
            inputProps={{
              maxLength: 6,
              pattern: '[0-9]*',
            }}
            sx={{ mb: 2 }}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={() => setShowVerificationDialog(false)}
            sx={{ mr: 2 }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleVerificationSubmit}
            variant="contained"
            disabled={loading || verificationCode.length !== 6}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            {loading ? 'Vérification...' : 'Vérifier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignIn; 